<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { Plus, Trash2, X } from 'lucide-vue-next'
import { baseQuantity } from '../types/procurement'
import type { ProductUom, QuantityEntry } from '../types/procurement'

/**
 * Right-side offcanvas for the detailed multi-UOM quantity of one order line:
 * the main UOM plus any additional units, converted to a base total.
 */
const props = defineProps<{
  open: boolean
  productName: string
  sku?: string | null
  /** Main UOM name (the product `unit`). */
  mainUom: string
  /** Additional UOMs configured for the product. */
  uoms: ProductUom[]
  quantities: QuantityEntry[]
}>()

const emit = defineEmits<{
  close: []
  apply: [quantities: QuantityEntry[]]
}>()

const { t } = useI18n()

const draft = ref<QuantityEntry[]>([])

watch(
  () => props.open,
  (isOpen) => {
    if (!isOpen) return

    const rows = props.quantities.length > 0 ? props.quantities : [{ uom_code: '', quantity: '' }]
    draft.value = rows.map((entry) => ({ ...entry }))
  },
)

const baseTotal = computed(() => baseQuantity(draft.value, props.uoms))

/**
 * Additional UOMs available for one row: the product's full `items[]` minus the
 * entries already taken by another row, so a unit is never picked twice.
 */
function optionsFor(index: number): ProductUom[] {
  const usedByOthers = new Set(
    draft.value.filter((_, position) => position !== index).map((entry) => entry.uom_code),
  )

  return props.uoms.filter((uom) => !usedByOthers.has(uom.uom_code))
}

function canAdd(): boolean {
  return optionsFor(draft.value.length).length > 0
}

function addEntry() {
  const next = optionsFor(draft.value.length)[0]
  if (!next) return

  draft.value.push({ uom_code: next.uom_code, quantity: '' })
}

function removeEntry(index: number) {
  // Row 0 is the main UOM and always stays.
  if (index <= 0) return
  draft.value.splice(index, 1)
}

function apply() {
  emit('apply', draft.value.map((entry) => ({ ...entry })))
  emit('close')
}
</script>

<template>
  <Teleport to="body">
    <div v-if="open" class="offcanvas-backdrop" @click.self="emit('close')">
      <aside class="offcanvas" role="dialog" aria-modal="true" :aria-label="t('procurement.form.detailedUom')">
        <header>
          <div>
            <p class="offcanvas__eyebrow">{{ t('procurement.form.quantity') }}</p>
            <h3>{{ productName || '—' }}</h3>
            <small v-if="sku">{{ sku }}</small>
          </div>
          <button class="offcanvas__close" type="button" :aria-label="t('procurement.common.cancel')" @click="emit('close')">
            <X :size="18" aria-hidden="true" />
          </button>
        </header>

        <div class="offcanvas__body">
          <label class="offcanvas__label">{{ t('procurement.form.detailedUom') }}</label>

          <div v-for="(entry, index) in draft" :key="index" class="offcanvas__row">
            <input
              v-model="entry.quantity"
              class="offcanvas__input"
              type="number"
              min="0"
              step="any"
              :placeholder="t('procurement.form.quantity')"
            >
            <span v-if="index === 0" class="offcanvas__uom offcanvas__uom--static">{{ mainUom || '—' }}</span>
            <select v-else v-model="entry.uom_code" class="offcanvas__uom">
              <option v-for="uom in optionsFor(index)" :key="uom.uom_code" :value="uom.uom_code">
                {{ uom.label || uom.uom_code }}
              </option>
            </select>
            <button
              v-if="index > 0"
              class="offcanvas__remove"
              type="button"
              :aria-label="t('procurement.form.removeLine')"
              @click="removeEntry(index)"
            >
              <Trash2 :size="15" aria-hidden="true" />
            </button>
            <span v-else class="offcanvas__spacer" />
          </div>

          <button v-if="canAdd()" class="btn-secondary offcanvas__add" type="button" @click="addEntry">
            <Plus :size="15" aria-hidden="true" />
            {{ t('procurement.form.addUom') }}
          </button>
        </div>

        <footer>
          <div class="offcanvas__total">
            <span>{{ t('procurement.form.totalInBase') }}</span>
            <strong>
              {{ baseTotal }}
              <small v-if="mainUom" class="offcanvas__uom-tag">{{ mainUom }}</small>
            </strong>
          </div>
          <div class="offcanvas__actions">
            <button class="btn-secondary" type="button" @click="emit('close')">{{ t('procurement.common.cancel') }}</button>
            <button class="btn-primary" type="button" @click="apply">{{ t('procurement.form.apply') }}</button>
          </div>
        </footer>
      </aside>
    </div>
  </Teleport>
</template>

<style scoped>
.offcanvas-backdrop {
  position: fixed;
  inset: 0;
  z-index: 950;
  background: rgb(43 38 32 / 45%);
}
.offcanvas {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto;
  width: min(430px, 100%);
  border-left: 1px solid #e2ddd4;
  background: #fff;
  box-shadow: -18px 0 40px rgb(43 38 32 / 18%);
  animation: offcanvas-in 160ms ease-out;
}
@keyframes offcanvas-in {
  from { opacity: 0.5; transform: translateX(24px); }
  to { opacity: 1; transform: none; }
}
@media (prefers-reduced-motion: reduce) {
  .offcanvas { animation: none; }
}
.offcanvas header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 16px;
  border-bottom: 1px solid #ece7de;
}
.offcanvas__eyebrow {
  margin: 0 0 2px;
  color: #857c70;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}
.offcanvas h3 { margin: 0; font-size: 15px; font-weight: 700; color: #2b2620; }
.offcanvas header small { color: #857c70; font-size: 12px; }
.offcanvas__close {
  display: inline-flex;
  padding: 4px;
  border: 0;
  border-radius: 8px;
  background: none;
  color: #5c5449;
  cursor: pointer;
}
.offcanvas__close:hover { background: #f5f2ec; }
.offcanvas__body {
  display: grid;
  align-content: start;
  gap: 10px;
  padding: 16px;
  overflow-y: auto;
}
.offcanvas__label {
  color: #5c5449;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}
.offcanvas__row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 130px 36px;
  gap: 8px;
  align-items: stretch;
}
.offcanvas__input,
.offcanvas__uom {
  min-width: 0;
  padding: 9px 10px;
  border: 1px solid #cdc6ba;
  border-radius: 8px;
  background: #fff;
  color: #2b2620;
  font: inherit;
  font-size: 13px;
}
.offcanvas__input { text-align: right; }
.offcanvas__input:focus,
.offcanvas__uom:focus { outline: 2px solid #765a30; outline-offset: 1px; }
.offcanvas__uom { color: #5c5449; font-weight: 600; }
.offcanvas__uom--static {
  display: inline-flex;
  align-items: center;
  background: #f5f2ec;
  pointer-events: none;
}
.offcanvas__remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e3c4bd;
  border-radius: 8px;
  background: #fff;
  color: #a13c2c;
  cursor: pointer;
}
.offcanvas__spacer { display: block; }
.offcanvas__add { justify-self: start; }
.offcanvas footer {
  display: grid;
  gap: 12px;
  padding: 16px;
  border-top: 1px solid #ece7de;
  background: #faf8f4;
}
.offcanvas__total {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  color: #6f675c;
  font-size: 13px;
}
.offcanvas__total strong { color: #2b2620; font-size: 16px; }
.offcanvas__uom-tag { margin-left: 4px; color: #857c70; font-size: 12px; font-weight: 500; }
.offcanvas__actions { display: flex; justify-content: flex-end; gap: 10px; }
</style>
